import { Capacitor, registerPlugin } from '@capacitor/core';
import { ENFORCE_SIGNING_CHECK, signingVerdict } from './signing-policy';
import { showTamperBlockScreen } from './block-screen';

/**
 * App signing check at start-up. The verdict rules live in ./signing-policy.js.
 *
 * OTA safety: bundles from this branch also run, by OTA, on store binaries
 * built before the native plugin existed. There `Capacitor.isPluginAvailable`
 * is false and this is a no-op — calling into a missing native plugin would
 * reject on every launch.
 *
 * Nothing here throws: a failure in the protection layer must not stop the
 * app from starting (the same rule src/utils/ota.js follows).
 */

/** The native plugin's registered name (SigningCheckPlugin.java / .swift). */
export const SIGNING_PLUGIN_NAME = 'SigningCheck';

const LOG_PREFIX = '[SigningCheck]';
const RUM_RETRY_MS = 3000;
const RUM_MAX_ATTEMPTS = 10;

/**
 * Send one event to Datadog RUM as a custom action. The check can finish before
 * RUM is initialised (both start at launch), and the RUM helper drops actions
 * until then, so retry for a while before giving up.
 */
export async function reportSigningEvent(name, context = {}, attempt = 1) {
  console.warn(LOG_PREFIX, name, context);
  try {
    // Dynamic, like ota.js: keeps the RUM bundle out of unit tests that do not
    // mock it, and avoids an import cycle through datadog -> app-version.
    const { addDatadogRumAction, isDatadogRumInitialized } = await import('@/utils/datadog');
    if (isDatadogRumInitialized()) {
      addDatadogRumAction(name, context);
      return;
    }
    if (attempt < RUM_MAX_ATTEMPTS) {
      setTimeout(() => reportSigningEvent(name, context, attempt + 1), RUM_RETRY_MS);
    }
  } catch (error) {
    console.warn(LOG_PREFIX, 'could not report to Datadog:', error?.message || error);
  }
}

/**
 * @param {object} [deps] - injectable for tests
 * @returns {Promise<{ verdict: string, blocked: boolean }>}
 */
export async function startSigningCheck({
  capacitor = Capacitor,
  loadPlugin = () => registerPlugin(SIGNING_PLUGIN_NAME),
  enforce = ENFORCE_SIGNING_CHECK,
  verdictFor = signingVerdict,
  showBlockScreen = showTamperBlockScreen,
  report = reportSigningEvent,
} = {}) {
  try {
    if (!capacitor.isNativePlatform()) return { verdict: 'web', blocked: false };
    if (!capacitor.isPluginAvailable(SIGNING_PLUGIN_NAME)) {
      return { verdict: 'plugin-unavailable', blocked: false };
    }

    const observed = await loadPlugin().check();
    const verdict = verdictFor(observed);
    if (verdict === 'match' || verdict === 'skipped') return { verdict, blocked: false };

    // Mismatch, unconfigured or error: report what the binary is signed with
    // (certificate hashes and Team IDs are public values, not secrets).
    const enforcing = enforce?.[observed.platform] === true;
    const blocked = verdict === 'mismatch' && enforcing;
    report('signing_check', { verdict, enforce: enforcing, blocked, ...observed });
    if (blocked) showBlockScreen({ platform: observed.platform });
    return { verdict, blocked };
  } catch (error) {
    report('signing_check_failed', { message: error?.message || String(error) });
    return { verdict: 'error', blocked: false };
  }
}
